<template>
  <article v-bind="$attrs">
    <SloModalView>
      <ais-instant-search
        class="bootstrap-reset"
        :search-client="$algolia.client"
        :index-name="$algolia.indexPrefix + indexName"
        :routing="routing"
        :future="{preserveSharedStateOnUnmount: true}"
      >
        <slo-configure v-bind="configuration" />
        <slot name="header" />
        <div class="container" v-if="!hideVariations">
          <div class="row">
            <div class="col col-12">
              <ProductResults>
                <template v-slot:request-spec-card>
                  <slot name="request-spec" />
                </template>
              </ProductResults>
            </div>
          </div>
          <ais-infinite-hits v-bind="$attrs" key="product-tabs">
            <template v-slot="{ items, isLastPage, refineNext, results }">
              <div class="row">
                <div v-if="items.length" class="col col-12 product-tabs no-overflow">
                  <SloTabs
                    class="mt-lg"
                    items="start"
                    :default-value="defaultTab(items)"
                  >
                    <SloTab
                      v-if="items[0]?.product_features || items[0]?.variation_features"
                      name="features"
                      :title="'Features' | t"
                    >
                      <section class="product-section product-features-list">
                        <h2 class="text-2xl mb-lg">
                          {{ 'Features' | t }}
                        </h2>
                        <div
                          v-html="items.length === 1 && items[0]?.variation_features ? items[0].variation_features : items[0].product_features"
                        />
                      </section>
                    </SloTab>
                    <SloTab v-if="items.length === 1" name="specifications" :title="'Specifications' | t">
                      <section id="specifications" class="product-section">
                        <h2 class="text-2xl mb-sm">
                          {{ 'Specifications' | t }}
                        </h2>
                        <h4 class="text-xl fw-strong mt-lg mb-md">
                          {{ 'Product Details' | t }}
                        </h4>
                        <div class="specifications-grid">
                          <div v-if="items[0].variation_product_type" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Product Type' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ items[0].variation_product_type }}
                            </span>
                          </div>
                          <div v-if="items[0].variation_part_type" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Part Type' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ items[0].variation_part_type }}
                            </span>
                          </div>
                          <div v-if="items[0].variation_product_lines" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Product Line' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ Array.isArray(items[0].variation_product_lines) ? items[0].variation_product_lines.join(', ') : '' }}
                            </span>
                          </div>
                          <div v-if="items[0].product_product_line" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Product Line' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ items[0].product_product_line }}
                            </span>
                          </div>
                          <div v-if="items[0].variation_bluetooth" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Bluetooth' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              Yes
                            </span>
                          </div>
                          <div v-if="items[0].variation_part_finish" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Finish' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ items[0].variation_part_finish }}
                            </span>
                          </div>
                          <div v-if="items[0].variation_finish" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Finish' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ items[0].variation_finish }}
                            </span>
                          </div>
                          <div v-if="items[0].variation_flow_rate_formatted" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Flow Rate' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ items[0].variation_flow_rate_formatted }}
                            </span>
                          </div>
                          <div v-if="items[0].product_mounting" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Mounting' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ items[0].product_mounting }}
                            </span>
                          </div>
                          <div v-if="items[0].variation_mounting" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Mounting Type' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ items[0].variation_mounting }}
                            </span>
                          </div>
                          <div v-if="items[0].variation_power_type" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Power Type' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ items[0].variation_power_type }}
                            </span>
                          </div>
                          <div v-if="items[0].variation_sensor_type" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Sensor Type' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ items[0].variation_sensor_type }}
                            </span>
                          </div>
                          <div v-if="items[0].variation_part_size" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Size' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ items[0].variation_part_size }}
                            </span>
                          </div>
                          <div v-if="items[0].variation_spray_type" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Spray Type' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ items[0].variation_spray_type }}
                            </span>
                          </div>
                          <div v-if="items[0].variation_trim_plate" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Trim Plate' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ items[0].variation_trim_plate }}
                            </span>
                          </div>
                          <div v-if="items[0].variation_gtin_upc" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'GTIN/UPC' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ items[0].variation_gtin_upc }}
                            </span>
                          </div>
                          <div v-if="items[0].variation_hazardous_materials" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Hazardous Materials' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              Yes
                            </span>
                          </div>
                        </div>
                        <h4
                          v-if="items[0].variation_length || items[0].variation_width || items[0].variation_height || items[0].variation_weight"
                          class="text-xl fw-strong mt-lg mb-md"
                        >
                          {{ 'Package Dimensions' | t }}
                        </h4>
                        <div
                          v-if="items[0].variation_length || items[0].variation_width || items[0].variation_height || items[0].variation_weight"
                          class="specifications-grid"
                        >
                          <div v-if="items[0].variation_length" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Length' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ items[0].variation_length }}
                            </span>
                          </div>
                          <div v-if="items[0].variation_width" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Width' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ items[0].variation_width }}
                            </span>
                          </div>
                          <div v-if="items[0].variation_height" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Height' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ items[0].variation_height }}
                            </span>
                          </div>
                          <div v-if="items[0].variation_weight" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Weight' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ items[0].variation_weight }}
                            </span>
                          </div>
                        </div>
                        <h4
                          v-if="productType !== 'accessory' && items[0].variation_compliances_certifications"
                          class="text-xl fw-strong mt-lg mb-md"
                        >
                          {{ 'Warranty/Certification' | t }}
                        </h4>
                        <div class="specifications-grid">
                          <div v-if="productType !== 'accessory'" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Limited Warranty' | t }}
                            </span>
                            <span class="flex-1 align-center h-100">
                              <a href="https://legal.sloan.com/warranty-policy.html">
                                {{ 'View Warranty Information' | t }}
                              </a>
                            </span>
                          </div>
                          <div v-if="items[0].variation_compliances_certifications" class="flex items-start">
                            <span class="flex-1 bg-gray-100 h-100">
                              {{ 'Compliances & Certifications' | t }}
                            </span>
                            <span class="flex-1 h-100">
                              {{ Array.isArray(items[0].variation_compliances_certifications) ? items[0].variation_compliances_certifications.join(', ') : items[0].variation_compliances_certifications }}
                            </span>
                          </div>
                        </div>
                      </section>
                    </SloTab>
                    <slot name="product-downloads" />
                    <slot name="product-videos" />
                  </SloTabs>
                </div>
              </div>
            </template>
          </ais-infinite-hits>
        </div>
        <!-- Virtual refinement list allows facets to be applied immediately without waiting for conditional filters -->
        <ais-refinement-list operator="and" attribute="search_api_language" class="sr-only" />
      </ais-instant-search>
    </SloModalView>
    <vue-snotify />
    <slot />
  </article>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import {findCartEntry} from '../../main/utils';
import t from '../../main/i18n';
import loadDropSpotFeaturesCarousel from '../drop-spot-features-carousel';
import store from '../../main/vue-store';
import router from '../../main/vue-routes';
import components from '../../algolia';
import ProductResults from './ProductResults.vue';
import SloModalView from '../../../lib/components/SloModalView.vue';
import SloTabs from '../../../lib/components/SloTabs.vue';
import SloTab from '../../../lib/components/SloTab.vue';

export default {
  comments: true,
  props: {
    analytics: {type: Object, required: true},
    productType: {type: String, required: true},
    productTitle: {type: String, required: true},
    hideVariations: {type: Boolean, required: false, default: false}
  },
  async mounted() {
    const currentPageHrefEndsWithDownloads = window.location.href.endsWith('#downloads');
    if (currentPageHrefEndsWithDownloads) {
      const element = document.getElementById('downloads');
      if (element) {
        setTimeout(() => element.scrollIntoView(), 1000);
      }
    }

    const searchParams = new URL(window.location.href).searchParams;
    const removeSkuFromCart = searchParams.get('removeSkuFromCart');

    if (removeSkuFromCart) {
      const cartEntryToRemove = findCartEntry(this.cartEntries, removeSkuFromCart);

      if (cartEntryToRemove) {
        await this.removeCartEntry({entryNumber: cartEntryToRemove?.entryNumber});
      }

      const newParams = {...searchParams.delete('removeSkuFromCart')};
      window.history.pushState(newParams, document.title, window.location.pathname);
    }

    loadDropSpotFeaturesCarousel();
  },
  data() {
    const indexName = `${this.$algolia.indexPrefix}${this.productType}_variations`;
    const router = this.$router;

    const refinementAlias = {
      sku: 'variation_sku'
    };

    const stateToRoute = uiState => {
      const indexUiState = uiState[indexName] || {};
      return {
        [indexName]: {
          refinementList: indexUiState.refinementList || {}
        }
      };
    };

    const routeToState = routeState => {
      return {
        [indexName]: {
          refinementList: routeState
        }
      };
    };

    return {
      routing: {
        router: {
          read() {
            const refinements = JSON.parse(JSON.stringify(router.currentRoute.query));

            let refinementList = {};
            for (let [key, value] of Object.entries(refinements)) {
              key = refinementAlias[key] || key;
              if (Array.isArray(value)) {
                refinementList[key] = value.map(val => decodeURIComponent(val));
              } else {
                refinementList[key] = [decodeURIComponent(value)];
              }
            }
            return refinementList;
          },
          write(routeState) {
            window.history.replaceState({}, null, this.createURL(routeState));
          },
          createURL(routeState) {
            const indexState = routeState[indexName] || {};

            const refinements = indexState.refinementList || {};
            let params = new URLSearchParams();
            for (const [key, values] of Object.entries(refinements)) {
              for (const value of Array.from(values)) {
                params.append(key, value);
              }
            }
            const paramStr = params.toString().replace(/\+/g, '%20');
            if (!paramStr) return window.location.pathname;

            return `${window.location.pathname}?${paramStr}`;
          },
          onUpdate(cb) {
            this._onPopState = ({state}) => {
              const routeState = state;
              if (!routeState) {
                cb(this.read());
              } else {
                cb(routeState);
              }
            };
            window.addEventListener('popstate', this._onPopState);
          },
          dispose() {
            window.removeEventListener('popstate', this._onPopState);
            this.write();
          }
        },
        stateMapping: {
          stateToRoute,
          routeToState
        }
      }
    };
  },
  computed: {
    ...mapGetters('marketplace', ['cartEntries']),
    indexName() {
      return `${this.productType}_variations`;
    },
    configuration() {
      return {
        filters: `(product_title:'${this.productTitle}')`,
        hitsPerPage: 3
      };
    }
  },
  methods: {
    ...mapActions('marketplace', ['removeCartEntry']),
    defaultTab(items) {
      if (items.length === 1 && (!items[0]?.product_features && !items[0]?.variation_features)) {
        return 'specifications';
      }

      return items[0]?.product_features || items[0]?.variation_features ? 'features' : 'specifications';
    }
  },
  provide() {
    return {
      productContext: {
        analytics: this.analytics,
        productType: this.productType,
        productTitle: this.productTitle
      }
    };
  },
  filters: {
    t
  },
  components: {
    ...components,
    ProductResults,
    SloModalView,
    SloTabs,
    SloTab
  },
  store,
  router
};
</script>
